import React from 'react';
import type { PersonRef, UsagePeriod } from '../../services/billingApi';

export const AI_TONE = '#18191D';
export const COMPUTE_TONE = '#B9BBC1';

export const PERIODS: { id: UsagePeriod; label: string }[] = [
  { id: 'period', label: 'This period' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'all', label: 'All time' },
];

export const usd = (value: number): string => {
  if (value > 0 && value < 0.01) return '<$0.01';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const exactUsd = (value: number): string => {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return usd(value);
};

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  DEPARTMENT_HEAD: 'Department Head',
  AI_CHAMPION: 'AI Champion',
  AI_PARTICIPANT: 'Employee',
  MEMBER: 'Employee',
  CONSULTANT: 'Partner',
  EXPERT: 'Expert',
  TEAM_LEAD: 'Team lead',
};

const titleCase = (value: string): string =>
  value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const roleLabel = (person: PersonRef): string => {
  if (person.kind === 'unlinked') return 'Background work and older calls';
  if (person.kind === 'former') return 'No longer in the team';
  let role = ROLE_LABEL[person.role || ''] || titleCase((person.role || 'member').toLowerCase());
  if (person.kind === 'invited') role = `${role}, invited`;
  if (person.department) return `${role} · ${person.department}`;
  return role;
};

export const displayName = (person: PersonRef): string => person.name || person.email || 'Not linked to a person';

const initials = (person: PersonRef): string => {
  if (person.kind === 'unlinked') return '··';
  const source = person.name || person.email || '?';
  const parts = source.replace(/@.*/, '').split(/[\s._+-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

export const Avatar: React.FC<{ person: PersonRef; size?: number }> = ({ person, size = 36 }) => {
  let tone = 'bg-canvas-inset text-ink';
  if (person.kind === 'unlinked' || person.kind === 'former') tone = 'bg-white text-ink-dim ring-1 ring-inset ring-line';
  if (person.role === 'OWNER') tone = 'bg-ink text-white';
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${tone}`}
      style={{ width: size, height: size, fontSize: size > 40 ? 16 : 12 }}
    >
      {initials(person)}
    </span>
  );
};

export const sinceLabel = (period: UsagePeriod, since: string | null): string => {
  if (period === 'all' || !since) return 'all time';
  const day = new Date(since).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (period === '30d') return 'the last 30 days';
  return `since ${day}`;
};

export const lastSeen = (iso: string | null): string => {
  if (!iso) return 'No usage yet';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'Active today';
  if (days === 1) return 'Active yesterday';
  if (days < 30) return `Active ${days} days ago`;
  return `Last active ${new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;
};
